import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaComments } from 'react-icons/fa';
import { useState } from 'react';

const PRIMARY_BLUE = '#0056d2'; 

// NOTE: Using a placeholder API endpoint based on the demo submission URL provided earlier.
// You must update this URL to your actual Contact Form submission endpoint.
const CONTACT_API_URL = 'https://aadhavacademy.com/api/auth/contact_request.php'; 

export default function Contact() {
    const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(null); // null, {type: 'success', message: '...'}, {type: 'error', message: '...'}

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Basic client-side validation
        if (!formData.name || !formData.email || !formData.subject || !formData.message) {
            setStatus({ type: 'error', message: '❌ Please fill in all fields.' });
            return;
        }

        setLoading(true);
        setStatus({ type: 'info', message: 'Sending your message...' });

        try {
            const response = await fetch(CONTACT_API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });

            // Attempt to parse JSON response for server-side messages
            const result = await response.json();

            if (response.ok) {
                // Successful submission
                setStatus({ 
                    type: 'success', 
                    message: `✅ Message sent successfully! ${result.message || 'We will respond within 24 hours.'}` 
                });
                // Clear the form only on success
                setFormData({ name: '', email: '', subject: '', message: '' });
            } else {
                // Handle API error response (e.g., validation failed on server)
                setStatus({ 
                    type: 'error', 
                    message: `❌ Failed to send message: ${result.message || 'Server error occurred.'}` 
                });
            }
        } catch (error) {
            // Handle network or connection errors
            setStatus({ 
                type: 'error', 
                message: `❌ Connection Error: The service is currently unavailable. Please try again later.` 
            });
            console.error('Contact Form Submission Error:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white py-5">
            <div className="container py-lg-5">
                
                {/* Header */}
                <header className="text-center mb-5">
                    <h1 className="display-5 fw-bold text-dark mb-3">Get in Touch with <span style={{ color: PRIMARY_BLUE }}>Our Team</span></h1>
                    <p className="lead text-secondary mx-auto" style={{ maxWidth: '800px' }}>
                        Whether you need technical support, have sales questions, or want a custom quote, we're here to help you scale your academy.
                    </p>
                </header>

                <div className="row g-5">
                    
                    {/* Contact Form */}
                    <div className="col-lg-7">
                        <div className="p-4 p-md-5 rounded-4 shadow-sm" style={{ border: '1px solid #eee' }}>
                            <h4 className="fw-bold mb-4 text-dark d-flex align-items-center"><FaComments className="me-2 text-muted" /> Send Us a Message</h4>
                            <form onSubmit={handleSubmit}>
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <input type="text" name="name" value={formData.name} onChange={handleChange} className="form-control form-control-lg rounded-3" placeholder="Your Full Name" required />
                                    </div>
                                    <div className="col-md-6">
                                        <input type="email" name="email" value={formData.email} onChange={handleChange} className="form-control form-control-lg rounded-3" placeholder="Work Email" required />
                                    </div>
                                    <div className="col-12">
                                        <input type="text" name="subject" value={formData.subject} onChange={handleChange} className="form-control form-control-lg rounded-3" placeholder="Subject (e.g., Sales Inquiry)" required />
                                    </div>
                                    <div className="col-12">
                                        <textarea name="message" value={formData.message} onChange={handleChange} className="form-control rounded-3" rows="5" placeholder="Your detailed message..." required></textarea>
                                    </div>
                                    <div className="col-12">
                                        {status && (
                                            <div className={`alert text-center small py-2 ${
                                                status.type === 'success' ? 'alert-success' : 'alert-danger'
                                            }`}>
                                                {status.message}
                                            </div>
                                        )}
                                        <button
                                            type="submit"
                                            className="btn text-white fw-bold btn-lg w-100 py-2 mt-2"
                                            style={{ background: PRIMARY_BLUE, border: 'none' }}
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Sending...
                                                </>
                                            ) : (
                                                'Submit Message'
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>

                    {/* Contact Info Sidebar */}
                    <div className="col-lg-5">
                        <div className="p-4 p-md-5 rounded-4 bg-light h-100">
                            <h4 className="fw-bold mb-4" style={{ color: PRIMARY_BLUE }}>Our Details</h4>
                            <ul className="list-unstyled small">
                                <li className="d-flex align-items-start mb-3">
                                    <FaEnvelope className="me-3 mt-1 text-muted" size={20} />
                                    <div>
                                        <p className="fw-semibold mb-0 text-dark">Email Support</p>
                                        <a href="mailto:support@aadhavsoftware.com" className="text-secondary text-decoration-none">admin@aadhavacademy.com</a>
                                    </div>
                                </li>
                                <li className="d-flex align-items-start mb-3">
                                    <FaPhone className="me-3 mt-1 text-muted" size={20} />
                                    <div>
                                        <p className="fw-semibold mb-0 text-dark">Sales Hotline</p>
                                        <a href="tel:+919876543210" className="text-secondary text-decoration-none">+91 95786 86886</a>
                                    </div>
                                </li>
                                <li className="d-flex align-items-start mb-3">
                                    <FaMapMarkerAlt className="me-3 mt-1 text-muted" size={20} />
                                    <div>
                                        <p className="fw-semibold mb-0 text-dark">Office Location</p>
                                        <span className="text-secondary">Cuddalore, Tamil Nadu, India </span>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}